import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  MapPin,
  Trash2,
  Eye,
  Pencil,
  Image as ImageIcon,
  BookOpen,
  Navigation,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTour } from '../contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function InfoHotspotsPanel({ 
  isOpen, 
  onClose, 
  infoHotspots = [],
  onRefresh,
  onNavigateToHotspot,
  onViewHotspot,
  onEditHotspot,
  onDeleteHotspot
}) {
  const { token, isAdmin } = useAuth();
  const { currentTour } = useTour();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleDelete = async () => {
    if (!deleteTarget || !token) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/info-hotspots/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        console.log('Deleted info hotspot:', deleteTarget.label);
        onDeleteHotspot?.(deleteTarget.id);
      }
    } catch (err) {
      console.error('Failed to delete hotspot:', err);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefresh?.();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Group hotspots by category
  const groupedHotspots = infoHotspots.reduce((acc, hotspot) => {
    const category = hotspot.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(hotspot);
    return acc;
  }, {});

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-[400px] sm:w-[450px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Info Hotspots
            </SheetTitle>
            <SheetDescription>
              Learning hotspots for {currentTour?.name || 'this tour'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex items-center justify-between">
            <Badge variant="outline" className="text-xs">
              {infoHotspots.length} hotspot{infoHotspots.length !== 1 ? 's' : ''}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <ScrollArea className="h-[calc(100vh-200px)] mt-4 pr-4">
            {infoHotspots.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MapPin className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm">No info hotspots yet</p>
                <p className="text-xs mt-1">Use &quot;Place Hotspot&quot; to create one</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedHotspots).map(([category, hotspots]) => (
                  <div key={category}>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {hotspots.map((hotspot) => (
                        <div
                          key={hotspot.id}
                          className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium truncate">{hotspot.label}</span>
                                {hotspot.image_url && (
                                  <ImageIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                )}
                              </div>
                              {hotspot.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {hotspot.description}
                                </p>
                              )}
                              <p className="text-[10px] text-muted-foreground/70 mt-1 font-mono">
                                x:{hotspot.position?.x?.toFixed(1)} y:{hotspot.position?.y?.toFixed(1)} z:{hotspot.position?.z?.toFixed(1)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => onViewHotspot?.(hotspot)}
                                title="View details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {isAdmin && onEditHotspot && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => onEditHotspot(hotspot)}
                                  title="Edit hotspot"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {onNavigateToHotspot && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => onNavigateToHotspot(hotspot)}
                                  title="Navigate to location"
                                >
                                  <Navigation className="h-4 w-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => setDeleteTarget(hotspot)}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete hotspot"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Info Hotspot?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteTarget?.label}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default InfoHotspotsPanel;
